import { Bell, CheckCircle, Eye, EyeOff, Loader2, Radio, RefreshCw, Stethoscope, TriangleAlert } from "lucide-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { getPlatformAdapter } from "../../platform/adapter"
import { type LiveStatusAvailability, getLiveStatusAdapter } from "../../platform/liveStatus"
import { LIVE_STATUS_POLICY_EVENT, notifyLiveStatusPolicyChanged } from "../../platform/liveStatusCoordinator"
import { type MobileDiagnosticsSnapshot, collectMobileDiagnostics } from "../../platform/mobileDiagnostics"
import { type MobileLiveStatusPolicy, getMobileLiveStatusPolicy } from "../../platform/mobilePolicy"
import { syncActivePushPreferences } from "../../platform/pushEnrollment"
import type { NotificationPermissionState, PlatformResult } from "../../platform/types"
import { Switch } from "../ui/Switch"

function resultMessage(result: Exclude<PlatformResult<unknown>, { status: "ok" }>): string {
    return result.status === "failed" ? result.message : result.reason
}

function permissionLabel(permission: NotificationPermissionState | null): string {
    if (permission === "granted") return "Allowed"
    if (permission === "denied") return "Not allowed"
    if (permission === "prompt") return "Not asked yet"
    return "Checking"
}

function liveStatusLabel(availability: LiveStatusAvailability | null, policy: MobileLiveStatusPolicy): string {
    if (policy.state === "unknown") return "Checking release availability"
    if (policy.state === "unavailable") return "Release policy is temporarily unavailable"
    if (!policy.liveStatusEnabled) return `Disabled for this release (${policy.reason})`
    if (!availability) return "Checking"
    if (!availability.enabled) return "Unavailable in system settings"
    if (availability.mode === "activitykit") return "Live Activities"
    if (availability.mode === "promoted") return "Promoted Live Updates"
    if (availability.mode === "ongoing") return "Ongoing notification"
    return "Unavailable"
}

function diagnosticTimestamp(value: string | null): string {
    if (!value) return "Not recorded"
    return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value))
}

function diagnosticAge(seconds: number | null): string {
    if (seconds === null) return "Not recorded"
    if (seconds < 60) return `${seconds}s ago`
    if (seconds < 3_600) return `${Math.floor(seconds / 60)}m ago`
    if (seconds < 86_400) return `${Math.floor(seconds / 3_600)}h ago`
    return `${Math.floor(seconds / 86_400)}d ago`
}

function diagnosticLiveStatus(snapshot: MobileDiagnosticsSnapshot): string {
    const live = snapshot.liveStatus
    if (live.state === "unavailable") return "Unavailable"
    const mode = live.availability?.mode ?? "unknown mode"
    const monitoring = live.monitoringEnabled === null ? "monitoring unknown" : live.monitoringEnabled ? "monitoring on" : "monitoring off"
    const surface = live.running === null ? "surface unknown" : live.running ? `surface ${live.lifecycle ?? "active"}` : "no active surface"
    return `${mode} · ${monitoring} · ${surface}`
}

function DiagnosticRow({ label, value, code = false }: { label: string; value: string; code?: boolean }) {
    return (
        <div className="grid grid-cols-[minmax(7rem,0.8fr)_minmax(0,1.2fr)] gap-3 px-3 py-2.5 max-[420px]:grid-cols-1 max-[420px]:gap-1">
            <dt className="text-xs text-muted">{label}</dt>
            <dd className={`min-w-0 break-words text-right text-xs text-base-content max-[420px]:text-left ${code ? "font-mono" : ""}`}>{value}</dd>
        </div>
    )
}

export function MobileNotificationsTab() {
    const platform = useMemo(() => getPlatformAdapter(), [])
    const liveStatus = useMemo(() => getLiveStatusAdapter(platform.kind), [platform])
    const [permission, setPermission] = useState<NotificationPermissionState | null>(null)
    const [availability, setAvailability] = useState<LiveStatusAvailability | null>(null)
    const [monitoring, setMonitoring] = useState(false)
    const [taskTitles, setTaskTitles] = useState(true)
    const [remotePolicy, setRemotePolicy] = useState<MobileLiveStatusPolicy>(() => getMobileLiveStatusPolicy())
    const [busy, setBusy] = useState<"permission" | "monitoring" | "privacy" | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [diagnostics, setDiagnostics] = useState<MobileDiagnosticsSnapshot | null>(null)
    const [diagnosticsBusy, setDiagnosticsBusy] = useState(false)
    const [diagnosticsError, setDiagnosticsError] = useState(false)

    const refreshDiagnostics = useCallback(async () => {
        setDiagnosticsBusy(true)
        setDiagnosticsError(false)
        try {
            setDiagnostics(await collectMobileDiagnostics())
        } catch {
            setDiagnosticsError(true)
        } finally {
            setDiagnosticsBusy(false)
        }
    }, [])

    useEffect(() => {
        let disposed = false
        void Promise.all([
            platform.getNotificationPermission(),
            liveStatus.getAvailability(),
            liveStatus.getMonitoringEnabled(),
            liveStatus.getTaskTitlesEnabled(),
        ]).then(([permissionResult, availabilityResult, monitoringResult, titlesResult]) => {
            if (disposed) return
            if (permissionResult.status === "ok") setPermission(permissionResult.value)
            else if (permissionResult.status === "denied") setPermission("denied")
            if (availabilityResult.status === "ok") setAvailability(availabilityResult.value)
            if (monitoringResult.status === "ok") setMonitoring(monitoringResult.value)
            if (titlesResult.status === "ok") setTaskTitles(titlesResult.value)
        })
        return () => {
            disposed = true
        }
    }, [liveStatus, platform])

    useEffect(() => {
        const refresh = () => setRemotePolicy(getMobileLiveStatusPolicy())
        window.addEventListener(LIVE_STATUS_POLICY_EVENT, refresh)
        refresh()
        return () => window.removeEventListener(LIVE_STATUS_POLICY_EVENT, refresh)
    }, [])

    useEffect(() => {
        void refreshDiagnostics()
    }, [refreshDiagnostics])

    const allowNotifications = async () => {
        setBusy("permission")
        setError(null)
        try {
            const result = await platform.requestNotificationPermission()
            if (result.status !== "ok") {
                if (result.status === "denied") setPermission("denied")
                setError(resultMessage(result))
                return
            }
            setPermission(result.value)
            if (result.value !== "granted") return
            const registration = await platform.registerForPushNotifications()
            if (registration.status !== "ok") setError(resultMessage(registration))
            const nextAvailability = await liveStatus.getAvailability()
            if (nextAvailability.status === "ok") setAvailability(nextAvailability.value)
        } finally {
            void refreshDiagnostics()
            setBusy(null)
        }
    }

    const changeMonitoring = async (enabled: boolean) => {
        setBusy("monitoring")
        setError(null)
        try {
            if (enabled && platform.kind === "android" && permission !== "granted") {
                const permissionResult = await platform.requestNotificationPermission()
                if (permissionResult.status !== "ok" || permissionResult.value !== "granted") {
                    setPermission("denied")
                    setError(permissionResult.status === "ok" ? "Notification permission is required for Android live status" : resultMessage(permissionResult))
                    return
                }
                setPermission("granted")
            }
            const result = await liveStatus.setMonitoringEnabled(enabled)
            if (result.status !== "ok") {
                setError(resultMessage(result))
                return
            }
            setMonitoring(result.value)
            notifyLiveStatusPolicyChanged()
            await syncActivePushPreferences()
        } catch {
            setError("The on-device setting was saved, but push delivery policy could not be synchronized yet.")
        } finally {
            void refreshDiagnostics()
            setBusy(null)
        }
    }

    const changeTaskTitles = async (enabled: boolean) => {
        setBusy("privacy")
        setError(null)
        try {
            const result = await liveStatus.setTaskTitlesEnabled(enabled)
            if (result.status !== "ok") {
                setError(resultMessage(result))
                return
            }
            setTaskTitles(result.value)
            notifyLiveStatusPolicyChanged()
            await syncActivePushPreferences()
        } catch {
            setError("The on-device privacy setting was saved, but push delivery policy could not be synchronized yet.")
        } finally {
            void refreshDiagnostics()
            setBusy(null)
        }
    }

    return (
        <div className="flex flex-col gap-8">
            <section>
                <h3 className="mb-1 text-base font-semibold text-base-content">Notifications</h3>
                <p className="mb-4 text-sm leading-6 text-muted">
                    Allow WildLark to tell you when work finishes, fails, or needs your input. Your prompts, transcripts, file contents, and access tokens are
                    never included in notification payloads.
                </p>
                <div className="flex items-center justify-between gap-4 border border-border bg-base-200/50 p-3 max-[600px]:items-start">
                    <div className="flex min-w-0 items-start gap-3">
                        <Bell size={17} className="mt-0.5 shrink-0 text-muted" />
                        <div>
                            <p className="text-sm font-medium text-base-content">System notifications</p>
                            <p className="mt-1 text-xs text-muted">{permissionLabel(permission)}</p>
                        </div>
                    </div>
                    {permission !== "granted" ? (
                        <button
                            type="button"
                            className="btn min-h-10 shrink-0 bg-primary px-3 text-sm font-medium text-primary-content hover:bg-primary/90 disabled:opacity-50 max-[600px]:min-h-11"
                            disabled={busy !== null || permission === null}
                            onClick={() => void allowNotifications()}
                        >
                            {busy === "permission" ? <Loader2 size={15} className="animate-spin" /> : "Allow"}
                        </button>
                    ) : (
                        <span className="flex shrink-0 items-center gap-1.5 text-xs font-medium text-success">
                            <CheckCircle size={14} /> Allowed
                        </span>
                    )}
                </div>
            </section>

            <section>
                <h3 className="mb-1 text-base font-semibold text-base-content">Live status</h3>
                <p className="mb-4 text-sm leading-6 text-muted">
                    Pin one glanceable view of running work to your lock screen. This is optional and can be stopped from the live view itself.
                </p>
                <div className="divide-y divide-border border border-border bg-base-200/50">
                    <div className="flex items-center justify-between gap-4 p-3">
                        <div className="flex min-w-0 items-start gap-3">
                            <Radio size={17} className="mt-0.5 shrink-0 text-muted" />
                            <div>
                                <p className="text-sm font-medium text-base-content">Monitor running work</p>
                                <p className="mt-1 text-xs text-muted">{liveStatusLabel(availability, remotePolicy)}</p>
                            </div>
                        </div>
                        <Switch
                            aria-label="Monitor running work"
                            checked={monitoring}
                            disabled={busy !== null || !availability?.enabled || !remotePolicy.liveStatusEnabled}
                            onCheckedChange={(enabled) => void changeMonitoring(enabled)}
                            className={{ root: "h-6 w-11 shrink-0", thumb: "h-5 w-5 data-[checked]:translate-x-5" }}
                        />
                    </div>
                    <div className="flex items-center justify-between gap-4 p-3">
                        <div className="flex min-w-0 items-start gap-3">
                            {taskTitles ? (
                                <Eye size={17} className="mt-0.5 shrink-0 text-muted" />
                            ) : (
                                <EyeOff size={17} className="mt-0.5 shrink-0 text-muted" />
                            )}
                            <div>
                                <p className="text-sm font-medium text-base-content">Show task titles</p>
                                <p className="mt-1 text-xs text-muted">Turn off for generic counts and status on the lock screen.</p>
                            </div>
                        </div>
                        <Switch
                            aria-label="Show task titles on the lock screen"
                            checked={taskTitles}
                            disabled={busy !== null}
                            onCheckedChange={(enabled) => void changeTaskTitles(enabled)}
                            className={{ root: "h-6 w-11 shrink-0", thumb: "h-5 w-5 data-[checked]:translate-x-5" }}
                        />
                    </div>
                </div>
            </section>

            <section>
                <div className="mb-1 flex items-center justify-between gap-3">
                    <h3 className="text-base font-semibold text-base-content">Support diagnostics</h3>
                    <button
                        type="button"
                        aria-label="Refresh support diagnostics"
                        className="btn flex min-h-11 min-w-11 items-center justify-center bg-transparent text-muted hover:bg-base-200 hover:text-base-content disabled:opacity-50"
                        disabled={diagnosticsBusy}
                        onClick={() => void refreshDiagnostics()}
                    >
                        <RefreshCw size={15} className={diagnosticsBusy ? "animate-spin" : ""} />
                    </button>
                </div>
                <p className="mb-4 text-sm leading-6 text-muted">
                    Local support state only. It never includes prompts, transcripts, credentials, provider tokens, or ActivityKit tokens.
                </p>
                {diagnostics ? (
                    <dl className="divide-y divide-border border border-border bg-base-200/50">
                        <DiagnosticRow
                            label="App"
                            value={`${diagnostics.platform.kind} ${diagnostics.platform.appVersion ?? "unknown"} (${diagnostics.platform.appBuild ?? "unknown build"})`}
                        />
                        <DiagnosticRow label="Node ID" value={diagnostics.connection.nodeId ?? "Not connected"} code />
                        <DiagnosticRow label="Connection" value={`${diagnostics.connection.status}${diagnostics.connection.suspended ? " · suspended" : ""}`} />
                        <DiagnosticRow label="Last cursor" value={diagnostics.connection.lastCursor ?? "Not received"} code />
                        <DiagnosticRow label="Last reconciliation" value={diagnosticTimestamp(diagnostics.lastSuccessfulReconciliationAt)} />
                        <DiagnosticRow label="Push permission" value={diagnostics.push.permission} />
                        <DiagnosticRow label="Push enrollment" value={`${diagnostics.push.state} · ${diagnostics.push.enrolledCount} active`} />
                        <DiagnosticRow
                            label="Installation"
                            value={diagnostics.push.installationIdSuffix ? `…${diagnostics.push.installationIdSuffix}` : "Not enrolled"}
                            code
                        />
                        <DiagnosticRow label="Provider token age" value={diagnosticAge(diagnostics.push.providerTokenAgeSeconds)} />
                        <DiagnosticRow
                            label="Live rollout"
                            value={`${diagnostics.liveStatus.remotePolicy.state} · ${diagnostics.liveStatus.remotePolicy.reason}${
                                diagnostics.liveStatus.remotePolicy.expiresAt
                                    ? ` · until ${diagnosticTimestamp(diagnostics.liveStatus.remotePolicy.expiresAt)}`
                                    : ""
                            }`}
                        />
                        <DiagnosticRow label="Live status" value={diagnosticLiveStatus(diagnostics)} />
                    </dl>
                ) : (
                    <div className="flex min-h-20 items-center justify-center gap-2 border border-border bg-base-200/50 p-4 text-sm text-muted">
                        {diagnosticsBusy ? <Loader2 size={16} className="animate-spin" /> : <Stethoscope size={16} />}
                        <span>{diagnosticsError ? "Diagnostics are temporarily unavailable." : "Reading on-device state…"}</span>
                    </div>
                )}
            </section>

            {error && (
                <div role="alert" className="flex items-start gap-2 border border-warning/30 bg-warning/10 p-3 text-sm text-warning">
                    <TriangleAlert size={16} className="mt-0.5 shrink-0" />
                    <span>{error}</span>
                </div>
            )}
        </div>
    )
}
